import React, { useState } from 'react';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import Avatar from '@/components/common/Avatar';
import ImageUploadCrop from '@/components/Forms/ImageUploadCrop';
import TextInput from '@/components/Forms/TextInput';
import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import { useForm } from '@inertiajs/react';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AccountDetails: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useInertiaData();
    const { route } = useRoutes();
    const [isEditing, setIsEditing] = useState(false);

    const { data, setData, post, processing, errors, clearErrors } = useForm({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        avatar: null as File | null,
    });

    if (!user) {
        return null;
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(route('my-account').toString(), {
            forceFormData: true,
            onSuccess: () => {
                setIsEditing(false);
            },
        });
    };

    const handleCancel = () => {
        setData({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            avatar: null,
        });
        clearErrors();
        setIsEditing(false);
    };

    const handleAvatarChange = (file: File) => {
        setData('avatar', file);
    };

    return (
        <>
            <div className="border-b border-gray-100 px-8 py-8">
                <div className="flex items-center space-x-6">
                    <div className="relative">
                        {isEditing ? (
                            <div className="flex flex-col items-center">
                                <ImageUploadCrop
                                    modelValue={user.avatar}
                                    buttonText={t('myAccount.changeAvatar')}
                                    onChange={handleAvatarChange}
                                    error={errors.avatar}
                                    rounded={true}
                                />
                            </div>
                        ) : (
                            <Avatar
                                user={user}
                                size="xl"
                                showOnlineIndicator={true}
                            />
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="mb-1 text-2xl font-semibold text-gray-900">
                                    {user.full_name}
                                </h1>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <div>
                                        {t('myAccount.joinedDate')}:{' '}
                                        {/* TODO: Add joined date */}
                                    </div>
                                    <div>{user.email}</div>
                                </div>
                            </div>
                            {!isEditing && (
                                <SecondaryButton
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    size="md"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                    {t('myAccount.editProfile')}
                                </SecondaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} translate="no">
                <div className="px-8 py-6">
                    <h3 className="mb-6 text-lg font-medium text-gray-900">
                        {t('myAccount.details')}
                    </h3>

                    <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
                        <div>
                            {isEditing ? (
                                <TextInput
                                    label={t('myAccount.firstName')}
                                    name="first_name"
                                    value={data.first_name}
                                    onChange={(value) =>
                                        setData('first_name', value)
                                    }
                                    placeholder={t('myAccount.firstName')}
                                    error={errors.first_name}
                                />
                            ) : (
                                <>
                                    <dt className="mb-1 text-sm font-medium text-gray-500">
                                        {t('myAccount.firstName')}
                                    </dt>
                                    <dd className="text-base text-gray-900">
                                        {user.first_name ||
                                            t('myAccount.notProvided')}
                                    </dd>
                                </>
                            )}
                        </div>

                        <div>
                            {isEditing ? (
                                <TextInput
                                    label={t('myAccount.nickname')}
                                    name="last_name"
                                    value={data.last_name}
                                    onChange={(value) =>
                                        setData('last_name', value)
                                    }
                                    placeholder={t('myAccount.nickname')}
                                    error={errors.last_name}
                                />
                            ) : (
                                <>
                                    <dt className="mb-1 text-sm font-medium text-gray-500">
                                        {t('myAccount.nickname')}
                                    </dt>
                                    <dd className="text-base text-gray-900">
                                        {user.last_name ||
                                            user.email ||
                                            t('myAccount.notProvided')}
                                    </dd>
                                </>
                            )}
                        </div>

                        <div>
                            {isEditing ? (
                                <TextInput
                                    label={t('home.email')}
                                    name="email"
                                    type="email"
                                    value={data.email}
                                    onChange={(value) =>
                                        setData('email', value)
                                    }
                                    placeholder={t('home.email')}
                                    error={errors.email}
                                />
                            ) : (
                                <>
                                    <dt className="mb-1 text-sm font-medium text-gray-500">
                                        {t('home.email')}
                                    </dt>
                                    <dd className="text-base text-gray-900">
                                        {user.email}
                                    </dd>
                                </>
                            )}
                        </div>
                    </div>

                    {isEditing && (
                        <div className="mt-6 flex gap-3">
                            <PrimaryButton
                                type="submit"
                                loading={processing}
                                size="md"
                            >
                                {t('common.saveChanges')}
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                onClick={handleCancel}
                                disabled={processing}
                                size="md"
                            >
                                {t('common.cancel')}
                            </SecondaryButton>
                        </div>
                    )}
                </div>
            </form>
        </>
    );
};

export default AccountDetails;
